import { EChatMessageSource, IChatMessage } from '@adaptiva/core/chat';
import { LoggingService } from '@adaptiva/core/logging';
import { EChatBubbleType } from '@adaptiva/widgets/chat';
import { HttpStatusCode } from '@angular/common/http';
import { computed, effect, EffectRef, inject, Injectable, linkedSignal, signal, Signal, WritableSignal } from '@angular/core';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import { LlmConversation, LlmConversationNode } from '../../classes';
import { ELlmResponseType } from '../../enums';
import { ILlmConversationNode, ILlmPayload, ILlmQueryRequest } from '../../interfaces';
import { AidaService } from '../aida';
import { LlmClient } from '../llm-client';
import { LlmForkService } from '../llm-fork';

@Injectable()
export class LlmChatService extends LlmClient {

	protected readonly aida: AidaService = inject(AidaService);

	protected readonly forkService: LlmForkService = inject(LlmForkService);

	protected readonly logger: LoggingService = inject(LoggingService);

	public readonly searchResults: WritableSignal<int[]> = signal([]);

	public readonly searchIndex: WritableSignal<int> = linkedSignal(() => {
		return !this.searchResults()?.length ? null : 0;
	});

	public readonly activeMessageIndex: WritableSignal<int> = linkedSignal(() => {
		return this.searchResults()[this.searchIndex()] ?? null;
	});

	public readonly messages: Signal<IChatMessage<LlmConversationNode>[]> = computed(() => {
		const _conversationRoot: LlmConversationNode = this.aida.conversationTree();
		const _activeFork: LlmConversationNode = this.forkService.activeFork();
		const _localMessages: IChatMessage<LlmConversationNode>[] = this.localMessages();	// When user sends a query but we haven't gotten a response back

		if (!_conversationRoot || !_activeFork)
			return [..._localMessages];

		const _messages: IChatMessage<LlmConversationNode>[] = this._messagesToId(_activeFork.nodeId, _conversationRoot) ?? [];

		if (_localMessages.length)
			_messages.push(..._localMessages);

		return _messages;
	});

	public readonly suggestedPrompts: WritableSignal<string[]> = linkedSignal(() => {
		return this.forkService.activeFork()?.suggestedPrompts || [];
	});

	public readonly errorMessages: WritableSignal<string[]> = linkedSignal(() => {
		this.aida.conversation();
		this.forkService.activeFork();

		return [];
	});

	protected readonly localMessages: WritableSignal<IChatMessage<LlmConversationNode>[]> = signal([]);

	protected readonly clearLocalMessagesFx: EffectRef = effect(() => {
		this.aida.conversation();
		this.forkService.activeFork();
		this.localMessages.set([]);
	});

	/**
	 * Find the instances of a user query
	 * @param _query user query to search for inside the messages
	 * @returns An array of indexes for results
	 */
	public find(_query: string): int[] {
		if (!_query)
			return [];

		return this.messages().reduce((_results: int[], _value: IChatMessage, _index: int) => {
			const _formattedMessage: string = _value.message?.toLowerCase();

			if (_formattedMessage.includes(_query?.toLowerCase()))
				_results.push(_index);

			return _results;
		}, []);
	}

	public goToNode(_node: LlmConversationNode): void {
		const _index: int = this.getNodeIndexById(_node.nodeId);

		if (_index > -1)
			this.activeMessageIndex.set(_index);
	}

	public getNodeIndexById(_nodeId: string): int {
		if (!_nodeId)
			return -1;

		let _nodeIndex: int = -1;
		const _hasNode: boolean = this.messages().some((_message: IChatMessage<LlmConversationNode<ILlmPayload>>, _index: int) => {
			_nodeIndex = _index;

			return _message.data.nodeId === _nodeId;
		});

		return _hasNode ? _nodeIndex : -1;
	}

	public sendMessage(_message: string): Observable<void> {
		if (!_message)
			return throwError(() => new Error('Creation request is empty'));

		this.addLocalMessage(_message, EChatMessageSource.QUERY);

		let _request: Observable<void>;

		if (this.aida.conversationTree())
			_request = this.query(_message);
		else
			_request = this.aida.createConversation(_message);

		return _request.pipe(
			catchError((_error: Error) => {
				const _errorMessage: string = this.logger.extractErrorMessage(_error);

				this.logger.error('LlmChatService.sendMessage', _errorMessage)();
				this.errorMessages.update((_messages: string[]) => {
					_messages.push(_errorMessage);

					return _messages;
				});

				return of(void 0);
			})
		);
	}

	public nodeMessages(_node: LlmConversationNode): IChatMessage<LlmConversationNode>[] {
		const _messages: IChatMessage<LlmConversationNode>[] = [];

		_messages.push({
			message: _node.userMessage,
			source: EChatMessageSource.QUERY,
			timestamp: _node.userMessageSentTimestamp,
			type: EChatBubbleType.TEXT,
			data: _node,
		});

		_messages.push({
			message: _node.llmResponse,
			source: EChatMessageSource.RESPONSE,
			timestamp: _node.llmResponseReceivedTimestamp,
			type: _node.responseType,
			data: _node,
		});

		return _messages;
	}

	/** Send a user query. Note, response should be handled by notifications */
	protected query(_query: string): Observable<void> {
		const _url: string = `${this.getBaseUrl()}/send-message`;
		const _target: LlmConversationNode = this.forkService.activeFork();
		const _request: ILlmQueryRequest = {
			conversationId: this.aida.conversation().objectId,
			conversationNodeId: _target?.nodeId || null,
			userQuery: _query
		};

		return this.post(_url, _request).pipe(
			map((_newNode: ILlmConversationNode) => {
				this.aida.addConversationNode(_target.nodeId, _newNode);
				this.aida.conversation.update(c => new LlmConversation(c));

				return void 0;
			}),
			catchError((_error: Error) => {
				this.addLocalMessage(this._errorMessage(_error), EChatMessageSource.RESPONSE);
				this.logger.defaultErrorHandler('LlmChatService.query', _error)();

				return of(void 0);
			})
		);
	}

	protected addLocalMessage(_message: string, _source: EChatMessageSource): void {
		this.localMessages.update((_prevMessages: IChatMessage<LlmConversationNode>[]) => {
			const _messages: IChatMessage<LlmConversationNode>[] = _prevMessages ?? [];

			return [
				..._messages,
				{
					message: _message,
					source: _source,
					timestamp: Date.now(),
					type: ELlmResponseType.TEXT,
					data: new LlmConversationNode({
						userMessage: _source === EChatMessageSource.QUERY ? _message : '',
						llmResponse: _source === EChatMessageSource.RESPONSE ? _message : '',
					})
				}
			];
		});
	}

	private _errorMessage(_error: Error): string {
		const _backendMessage: string = this.logger.extractErrorMessage(_error);

		if (_backendMessage)
			return _backendMessage;

		const _errorCode: int = this.logger.extractErrorCode(_error);

		switch (_errorCode) {
			case HttpStatusCode.NotFound:
				return 'Could not reach the server, please try again later';
			default:
				return 'Error generating the dashboard, please try a new query!';
		}
	}

	private _messagesToId(_targetId: string, _node: LlmConversationNode): IChatMessage<LlmConversationNode>[] | null {
		if (!_node || !_targetId)
			return null;

		if (_targetId === _node.nodeId)
			return this.nodeMessages(_node);

		let _path: IChatMessage<LlmConversationNode>[] = null;

		_node.children?.some?.((_childNode: LlmConversationNode) => {
			const _childPath: IChatMessage<LlmConversationNode>[] | null = this._messagesToId(_targetId, _childNode);

			if (_childPath) {
				_path = [this.nodeMessages(_node), _childPath].flat();

				return true;
			}

			return false;
		});

		return _path;
	}

}
